import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { ModuleHeader } from '../shared/formFields';
import { formatDate, formatDateTime } from '../../kit/time';
import { toInputDate, plausible } from '../shared/dateInput';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { AriveLockNote, isAriveManaged, toastAriveConflict } from '../../ariveManaged';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ChecksList } from '../shared/ChecksList';
import { promptDialog } from '@/components/dialogs/DialogHost';

// Who did what on this loan. The server decides the tier: staff see edits, status
// moves, uploads and sends; owners and admins also see downloads, deletes and SSN
// reveals (marked). Field changes name the fields, never the values.
function AuditTrail({ loanId }) {
  const [events, setEvents] = useState([]);
  const [next, setNext] = useState(null);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (before) => {
    setLoading(true);
    setError(false);
    try {
      const res = await losApi.loanAudit(loanId, before ? { before } : {});
      setEvents((prev) => (before ? [...prev, ...res.events] : res.events));
      setNext(res.next_before || null);
      setTier(res.tier);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="audit-trail">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>Audit trail</div>
        {tier && (
          <div className="text-[11px] text-slate-500" data-testid="audit-trail-tier">
            {tier === 'admin' ? 'Full trail, including sensitive actions' : 'Edits, status moves, uploads and sends'}
          </div>
        )}
      </div>
      {error && (
        <div className="px-5 py-4 text-[12px] text-red-700" data-testid="audit-trail-error">
          The audit trail did not load.{' '}
          <button type="button" className="font-semibold underline" onClick={() => load()}>Retry</button>
        </div>
      )}
      {!error && !loading && events.length === 0 && (
        <div className="px-5 py-4 text-[12px] text-slate-400 italic">Nothing recorded on this loan yet.</div>
      )}
      <ul className="divide-y divide-slate-100">
        {events.map((e) => (
          <li key={e.event_id} className="px-5 py-2.5 flex items-start gap-3 text-[12px]" data-testid="audit-trail-row">
            <div className="w-[150px] shrink-0 text-slate-500">{formatDateTime(e.ts)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold flex items-center gap-1.5" style={{ color: '#030524' }}>
                {e.sensitive && <ShieldAlert size={12} className="text-amber-600" aria-label="Sensitive action" />}
                {e.title}
              </div>
              {e.detail && <div className="text-slate-500 break-words">{e.detail}</div>}
            </div>
            <div className="text-slate-500 shrink-0">{e.actor || '—'}</div>
          </li>
        ))}
      </ul>
      {loading && (
        <div className="px-5 py-3 text-slate-400"><Loader2 size={14} className="animate-spin" /></div>
      )}
      {!loading && !error && next && (
        <div className="px-5 py-3 border-t border-slate-100">
          <button type="button" onClick={() => load(next)} className="text-[12px] font-semibold hover:underline" style={{ color: '#070B5F' }} data-testid="audit-trail-older">
            Load older
          </button>
        </div>
      )}
    </div>
  );
}

// The TRID dates (ticket 192 D27): typed here on a BFY-originated loan; on an
// Arive-linked loan they are Arive's and show read-only. A picked or fully typed date
// saves at once; clearing it saves when the field loses focus (an empty value is
// also what a half-typed date reads as, so that is checked against `badInput`).
const DATE_FIELDS = {
  le_issued_date: { label: 'LE Sent', testId: 'le-date', saved: 'LE date saved' },
  intent_to_proceed_date: { label: 'Intent to proceed', testId: 'intent-date', saved: 'Intent to proceed date saved' },
  cd_issued_date: { label: 'CD Sent', testId: 'cd-date', saved: 'CD date saved' },
  // Ticket 241: blank means receipt is presumed 3 business days after the CD was sent
  // (Reg Z 1026.19(f)(1)(iii)); enter the day for a CD handed over or e-signed sooner.
  cd_received_date: {
    label: 'CD Received',
    testId: 'cd-received-date',
    saved: 'CD received date saved',
    hint: 'Blank: presumed 3 business days after sent',
  },
  consummation_date: { label: 'Closing (consummation)', testId: 'closing-date', saved: 'Closing date saved' },
};

function DateRow({ loan, field, onChange }) {
  const { label, testId, saved, hint } = DATE_FIELDS[field];
  const locked = isAriveManaged(loan);
  const stored = toInputDate(loan[field]);
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(stored); }, [stored]);

  // On a loan already at Closing or Funded, a date that shortens the CD waiting period
  // is held like the move itself (D28); an owner or admin may save it with a reason.
  const put = async (value) => {
    const body = { [field]: value || null };
    try {
      await losApi.updateLoan(loan.loan_file_id, body);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status !== 409 || d?.code !== 'trid_cd_waiting_period' || !d.can_override) throw e;
      const reason = await promptDialog({
        title: 'Override the TRID hold and save this date?',
        description: d.message,
        label: 'Reason (kept in the audit trail)',
        placeholder: 'Why this loan closes inside the waiting period',
        multiline: true,
        confirmText: 'Override and save',
      });
      if (!reason) return false;
      await losApi.updateLoan(loan.loan_file_id, { ...body, trid_override_reason: reason });
    }
    return true;
  };

  const save = async (value) => {
    if (saving || value === stored) return;
    setSaving(true);
    try {
      if (!(await put(value))) {
        setDraft(stored);
        setSaving(false);
        return;
      }
      toast({ title: saved });
    } catch (e) {
      setDraft(stored);
      if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) });
      setSaving(false);
      return;
    }
    try { if (onChange) await onChange(); } catch { /* the loan file shows its own load error */ }
    setSaving(false);
  };
  const edit = (e) => {
    setDraft(e.target.value);
    if (plausible(e.target.value)) save(e.target.value);
  };
  const leave = (e) => {
    if (e.target.validity?.badInput || (draft && !plausible(draft))) setDraft(stored);
    else if (draft === '' && stored) save('');
  };
  const inputId = `${testId}-input`;
  const shownHint = hint && !stored ? <span className="block text-[11px] text-slate-400" data-testid={`${testId}-hint`}>{hint}</span> : null;
  return (
    <div className="flex items-center justify-between px-5 py-3 text-[13px]">
      {locked ? (
        <>
          <span className="text-slate-500">{label}{shownHint}</span>
          <span className="font-semibold" style={{ color: '#030524' }} data-testid={`${testId}-locked`}>{loan[field] ? formatDate(loan[field]) : '—'}</span>
        </>
      ) : (
        <>
          <label htmlFor={inputId} className="text-slate-500">{label}{shownHint}</label>
          <span className="flex items-center gap-2">
            {saving && <Loader2 size={12} className="animate-spin text-slate-400" role="status" aria-label="Saving" />}
            <Input
              id={inputId}
              type="date"
              data-testid={inputId}
              className="h-8 w-auto px-2 text-[13px] md:text-[13px] font-semibold"
              style={{ color: '#030524' }}
              value={draft}
              onChange={edit}
              onBlur={leave}
            />
          </span>
        </>
      )}
    </div>
  );
}

// HMDA action taken (Reg C), as LoanFile.hmda_action stores it.
const HMDA_ACTIONS = [
  ['originated', 'Originated'],
  ['denied', 'Denied'],
  ['withdrawn', 'Withdrawn'],
  ['app_incomplete', 'Application incomplete'],
  ['closed_incomplete', 'File closed for incompleteness'],
  ['purchased', 'Purchased loan'],
];
const HMDA_LABELS = Object.fromEntries(HMDA_ACTIONS);

function HmdaActionRow({ loan, onChange }) {
  const locked = isAriveManaged(loan);
  const [saving, setSaving] = useState(false);
  const save = async (value) => {
    if (saving || value === (loan.hmda_action || '')) return;
    setSaving(true);
    try {
      await losApi.updateLoan(loan.loan_file_id, { hmda_action: value });
      toast({ title: 'HMDA action saved' });
      try { if (onChange) await onChange(); } catch { /* the loan file shows its own load error */ }
    } catch (e) {
      if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };
  const shown = HMDA_LABELS[loan.hmda_action] || loan.hmda_action;
  return (
    <div className="flex items-center justify-between px-5 py-3 text-[13px]">
      <span className="text-slate-500">HMDA action taken</span>
      {locked ? (
        <span className="font-semibold" style={{ color: '#030524' }} data-testid="hmda-action-locked">{shown || '—'}</span>
      ) : (
        <span className="flex items-center gap-2">
          {saving && <Loader2 size={12} className="animate-spin text-slate-400" role="status" aria-label="Saving" />}
          <Select value={loan.hmda_action || ''} onValueChange={save} disabled={saving}>
            <SelectTrigger className="h-8 w-[240px] text-[13px] font-semibold" data-testid="hmda-action-select" aria-label="HMDA action taken">
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent>
              {HMDA_ACTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </span>
      )}
    </div>
  );
}

// The loan's TRID / HMDA checks (ticket 192 D27), green or red, refetched whenever
// the loan is saved. The same findings feed the move-status confirm.
function ChecksPanel({ loan }) {
  const loanId = loan.loan_file_id;
  const [checks, setChecks] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setError(false);
    losApi.loanChecks(loanId)
      .then((c) => { if (live) setChecks(c); })
      .catch(() => { if (live) setError(true); });
    return () => { live = false; };
  }, [loanId, loan.updated_at, attempt]);

  const counts = checks?.counts || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="loan-checks-panel">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>TRID and HMDA checks</div>
        {checks && (
          <div className="text-[11px] text-slate-500" data-testid="loan-checks-counts">
            {counts.fail || 0} failing · {counts.warn || 0} to look at · {counts.pass || 0} passing
          </div>
        )}
      </div>
      <div className="px-5 py-3">
        {error ? (
          <div className="text-[12px] text-red-700" data-testid="loan-checks-error">
            The checks did not load.{' '}
            <button type="button" className="font-semibold underline" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
          </div>
        ) : !checks ? (
          <Loader2 size={14} className="animate-spin text-slate-400" />
        ) : checks.findings.length === 0 ? (
          <div className="text-[12px] text-slate-400 italic">No checks apply to this loan yet.</div>
        ) : (
          <ChecksList findings={checks.findings} />
        )}
      </div>
    </div>
  );
}

export function AuditDatesModule({ loan, onChange }) {
  // The TRID dates are date-only (like close_date); the rest carry a real time, so
  // they get the full local-zone timestamp.
  const rows = [
    ['Created', loan.created_at],
    ['Last Updated', loan.updated_at],
    'le_issued_date',
    'intent_to_proceed_date',
    'cd_issued_date',
    'cd_received_date',
    'consummation_date',
    // closed_at is stamped when the status enters a terminal state; only a funded
    // loan's counts as the funding date (cancelled / denied / withdrawn close too).
    ['Funded', loan.status === 'funded' ? loan.closed_at : null],
  ];
  return (
    <div className="space-y-4">
      <ModuleHeader title="Audit & Dates" />
      <AriveLockNote loan={loan} testId="audit-arive-note">The TRID dates and the HMDA action are edited in Arive.</AriveLockNote>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {rows.map((row) => (typeof row === 'string' ? <DateRow key={row} loan={loan} field={row} onChange={onChange} /> : (
          <div key={row[0]} className="flex items-center justify-between px-5 py-3 text-[13px]">
            <span className="text-slate-500">{row[0]}</span>
            <span className="font-semibold" style={{ color: '#030524' }}>{row[1] ? formatDateTime(row[1]) : '—'}</span>
          </div>
        )))}
        <HmdaActionRow loan={loan} onChange={onChange} />
      </div>
      <ChecksPanel loan={loan} />
      <AuditTrail loanId={loan.loan_file_id} />
    </div>
  );
}
